import {Button, Segmented} from 'antd'
import {post_create_Api} from 'apis'
import {getId, getType} from 'apis/model/base'
import {createValue} from 'components/form/utils'
import {Field, Formik, useFormikContext} from 'formik'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import React, {useCallback, useContext, useMemo, useRef} from 'react'
import {nest} from 'recompose'
import {NotificationActionTypes, successNotify} from 'views/AppLocals/factory/createNotification'
import {NullAsDefault} from 'views/Shared'
import {notifyOnError} from '../../../views/AppLocals/factory/createErrorEvent'
import {LayoutContext} from '../../layouts/Default/LayoutContext'
import MentionDataContext, {MentionDataProvider} from '../MentionDataContext'
import MegaTextarea from './MegaTextarea'
import {PostPrivacyTypes} from './PostPrivacy'
import LookupField from '../../LookupField'
import {PostFormContext} from '../PostForm/PostFormContext'
import PostSettingForm from '../PostForm/PostSettingForm'

// ─── PostForm (inner — uses Formik context) ───────────────────────────────────

const PostForm = ({ onCancel }) => {
    const t = useTranslate()
    const { isLg } = useContext(LayoutContext)
    const { handleSubmit, isValid, values, handleChange } = useFormikContext()
    const { push = NullAsDefault } = useContext(MentionDataContext)

    return (
        <div className="flex flex-col gap-4">
            {/* Header row */}
            <div className="flex items-center justify-between lg:justify-center gap-2">
                {!isLg && (
                    <Button
                        onClick={onCancel}
                        className="button-rounded-md no-border text-xs text-color-100">
                        {t('close')}
                    </Button>
                )}
                <div className="font-bold text-color-000 uppercase leading-tight">
                    {values?.post_type === 'prayer' ? t('intention') : t('posts')}
                </div>
                {!isLg && (
                    <Button
                        type="primary"
                        onClick={() => handleSubmit()}
                        disabled={!isValid}
                        className="button-rounded-md no-border text-sm px-3">
                        {t('publish a post')}
                    </Button>
                )}
            </div>

            {/* Form body */}
            <div className="flex flex-col gap-3">
                {/* Channel / creator / privacy selector */}
                <PostSettingForm />

                {/* Post type toggle + prayer category */}
                <div className="lex flex-col space-y-2 gap-3 background rounded-lg">
                    <Segmented
                        value={values?.post_type}
                        onChange={(value) => handleChange(createValue('post_type', value))}
                        options={[
                            { label: t('news post'),         value: 'post'   },
                            { label: t('prayer intention'),  value: 'prayer' },
                        ]}
                        className="custom-segmented-secure w-fit"
                    />
                    {values?.post_type === 'prayer' && (
                        <LookupField
                            createIfNotExists={true}
                            defaultValue={values?.prayer_category}
                            apiPath="/lookup/prayer_category/gets"
                            placeholder={t('prayer type')}
                            name="prayer_category"
                            renderOption={(o, i) => (
                                <div key={i} className="flex flex-col justify-center flex-1">
                                    <span className="font-semibold text-sm text-color-000 tracking-wide leading-snug">
                                        {o?.label}
                                    </span>
                                    {o?.description && (
                                        <span className="text-xs text-color-300 italic tracking-wider leading-snug">
                                            {o?.description}
                                        </span>
                                    )}
                                </div>
                            )}
                            handleChange={(e) => {
                                handleChange(createValue('prayer_category', e.target.value))
                            }}
                        />
                    )}
                </div>

                {/* Content textarea */}
                <Field name="description">
                    {({ field }) => (
                        <MegaTextarea
                            {...field}
                            rows={4}
                            placeholder={t('Tap here to write content. \nType @ to mention')}
                            style={{ outline: 'none !important', resize: 'none' }}
                            fieldNames={{ embed_data: 'embed_data', description: 'description' }}
                            onMentioned={(mention) => push(mention)}
                        />
                    )}
                </Field>
            </div>

            {/* Footer (desktop) */}
            {isLg && (
                <div className="flex justify-end gap-4 px-0 md:px-3">
                    <Button
                        onClick={onCancel}
                        className="button-rounded-md no-border text-color-200">
                        {t('close')}
                    </Button>
                    <Button
                        color="primary"
                        variant="solid"
                        onClick={() => handleSubmit()}
                        disabled={!isValid}
                        className="button-rounded-md no-border">
                        {t('publish a post')}
                    </Button>
                </div>
            )}
        </div>
    )
}

// ─── CreatePostForm ───────────────────────────────────────────────────────────

const CreatePostForm = ({ initialValues, onCancel = NullAsDefault }) => {
    const t = useTranslate()
    const history = useHistory()
    const { options, current } = useContext(PostFormContext)
    const submittingRef = useRef(false)
    const { toString = NullAsDefault } = useContext(MentionDataContext)

    // Build Formik initial values from PostFormContext.current
    // current = { container: channelObj, creator: ownerObj, privacy }
    const _initialValues = useMemo(() => {
        const { container, creator, privacy } = current ?? {}

        const defaultChannelOption = container
            ? options.find((o) => o.value === getId(container)) ?? null
            : _.first(options) ?? null

        return {
            containers: options,
            privacy: privacy ?? PostPrivacyTypes.public.value,
            post_type: 'post',
            ...(initialValues ?? {}),
            // Formik shape for container: { value, data, id, type, label }
            container: defaultChannelOption ?? undefined,
            // Formik shape for owner: { value, data, id, type, label }
            owner: creator
                ? {
                    value: getId(creator),
                    data:  creator,
                    id:    getId(creator),
                    type:  getType(creator),
                    label: creator?.name ?? creator?.username ?? '',
                }
                : undefined,
        }
    }, [current, options])

    const onSuccess = (__, { response }) => {
        submittingRef.current = false
        onCancel()
        successNotify(NotificationActionTypes.CREATE, t)
        history.push({ state: { refreshToken: Date.now() } })
    }

    const onError = (...args) => {
        submittingRef.current = false
        notifyOnError(t)(...args)
    }

    const { handleAsyncAction: createPost = NullAsDefault } = useAsyncAction({
        apiInfo: post_create_Api,
        onError,
        onSuccess,
    })

    const onSubmit = ({ tags, description, ...rest }) => {
        if (submittingRef.current) return
        submittingRef.current = true
        const _tags = _.isArray(tags) ? Array.from(tags) : undefined
        createPost(
            {
                ...rest,
                tags: _tags ? _tags.join(',') : undefined,
                description: toString(description),
            },
            { id: rest.container_id, prop: rest.container_type }
        )
    }

    const initialErrors = {
        description: t('min length 2 characters'),
    }

    const validate = useCallback((values) => {
        const errors = {}
        const { description, post_type, prayer_category } = values ?? {}

        if (post_type === 'prayer' && _.isNil(prayer_category ?? undefined)) {
            errors.prayer_category = t('required field')
        }

        _.forEach(['owner', 'container'], (name) => {
            if (_.isNil(_.get(values, name) ?? undefined)) {
                errors[name] = t('required field')
            }
        })

        if (String(description ?? '').trim().length < 2) {
            errors.description = initialErrors.description
        }

        return errors
    }, [])

    return (
        <Formik
            onSubmit={(values) => {
                const {
                    owner, container, containers,
                    default_owner_id, default_container_id,
                    isLoading,
                    ...rest
                } = values ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            owner_id:       getId(owner?.data),
                            owner_type:     getType(owner?.data),
                            container_id:   getId(container?.data),
                            container_type: getType(container?.data),
                        },
                        _.isUndefined
                    )
                )
            }}
            validate={validate}
            enableReinitialize={true}
            initialErrors={initialErrors}
            initialValues={_initialValues}>
            <PostForm onCancel={onCancel} />
        </Formik>
    )
}

export default nest(MentionDataProvider, CreatePostForm)